import { useState } from 'react'
import * as Yup from 'yup'
import { Alert, Button, DrawerStepper, InfoIcon } from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import {
  useValidationErrors,
  onSuccessMessage,
  onErrorMessage,
} from '@/helpers'
import { useMergeCatalogProducts } from '@/services'
import { CatalogProductProps } from '@/types'

import styles from './drawer-form.module.css'

import { DrawerFormMergeProps } from './drawer-form-merge.type'
import { CatalogSearch } from '@/components'

export const DrawerFormMerge = ({ isOpen, onClose }: DrawerFormMergeProps) => {
  const { t } = useTranslation('screenCatalogProduct')

  const [primaryCatalogProduct, setPrimaryCatalogProduct] = useState<
    Partial<CatalogProductProps> | undefined
  >(undefined)
  const [secondaryCatalogProduct, setSecondaryCatalogProduct] = useState<
    Partial<CatalogProductProps> | undefined
  >(undefined)

  const {
    mutateAsync: mergeCatalogProducts,
    isPending: isLoadingMergeCatalogProducts,
  } = useMergeCatalogProducts()

  const onSelectCatalogProduct = (
    isMain: boolean,
    catalogProduct?: Partial<CatalogProductProps>,
  ) => {
    if (isMain) {
      setPrimaryCatalogProduct(catalogProduct)
    } else {
      setSecondaryCatalogProduct(catalogProduct)
    }
  }

  const { errors, isValid, onHandleListenErrors } = useValidationErrors(
    {
      primaryCatalogProduct: Yup.object().shape({
        reference: Yup.string().required('drawerCatalogMergeRequiredMessage'),
      }),
      secondaryCatalogProduct: Yup.object().shape({
        reference: Yup.string().required('drawerCatalogMergeRequiredMessage'),
      }),
    },
    {
      primaryCatalogProduct,
      secondaryCatalogProduct,
    },
  )

  const onSubmit = async () => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    const response = await mergeCatalogProducts({
      primaryProductReference: primaryCatalogProduct?.reference || '',
      secondaryProductReference: secondaryCatalogProduct?.reference || '',
    })

    if (response.success) {
      onClose()
      onSuccessMessage(t('drawerCatalogMergeSuccess'))
    } else {
      onErrorMessage(t('drawerCatalogMergeError'), response.message)
    }
  }

  return (
    <DrawerStepper
      initialStep="root"
      isOpen={isOpen}
      onHandleOpen={onClose}
      translations={{
        optional: t('drawerCatalogMergeOptional'),
      }}
      title={t('drawerCatalogMergeTitle')}
      steps={{
        root: {
          backNavigate: 'root',
          component: (
            <div className={styles.container}>
              <Alert
                title={t('drawerCatalogStepTitle')}
                variant="primary"
                leftIcon={<InfoIcon />}
              >
                {t('drawerCatalogStepDescription')}
              </Alert>
              <div className={styles.field}>
                <CatalogSearch
                  title={t('drawerCatalogMergePrimaryProduct')}
                  onSelectProduct={(product) =>
                    onSelectCatalogProduct(true, product)
                  }
                  selectedProduct={primaryCatalogProduct as CatalogProductProps}
                  error={
                    (errors?.primaryCatalogProduct?.reference as string) ||
                    (errors?.primaryCatalogProduct as string)
                  }
                />
              </div>

              <div className={styles.field}>
                <CatalogSearch
                  title={t('drawerCatalogMergeSecondaryProduct')}
                  onSelectProduct={(product) =>
                    onSelectCatalogProduct(false, product)
                  }
                  selectedProduct={
                    secondaryCatalogProduct as CatalogProductProps
                  }
                  error={
                    (errors?.secondaryCatalogProduct?.reference as string) ||
                    (errors?.secondaryCatalogProduct as string)
                  }
                />
              </div>
            </div>
          ),
          isOptional: false,
          footer: (
            <Button onClick={onSubmit} disabled={isLoadingMergeCatalogProducts}>
              {t('drawerCatalogStepButton')}
            </Button>
          ),
        },
      }}
    />
  )
}
